import React from "react";
import { Button, Text, Box, useMediaQuery } from "@salestrak/ui";
import { useRouter } from '#app/lib/hooks/router-compat';
import { SalesTrakLogo } from "#app/components/brand/salestrak-logo";
import { LegalLinks } from "./legal-links";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

const quotes = [
  {
    text: "Salestrak CRM has streamlined our workflows like never before.",
    author: "@Jessica_P",
  },
  {
    text: "I love how easy it is to track deals and collaborate with my team.",
    author: "@Mike_R",
  },
  {
    text: "The dashboard gives me instant insights into our sales pipeline.",
    author: "@Priya_S",
  },
  {
    text: "Task management in Salestrak keeps our team on schedule.",
    author: "@Carlos_T",
  },
  {
    text: "Custom reports help us make data-driven decisions every week.",
    author: "@Linda_W",
  },
  {
    text: "Integrating Salestrak with our email was seamless and saved us hours.",
    author: "@Alex_K",
  },
  {
    text: "The mobile app lets me make updates on the go.",
    author: "@Sophie_L",
  },
];

function useRotatingQuote() {
  const [index, setIndex] = React.useState(() =>
    Math.floor(Math.random() * quotes.length),
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => {
        let next: number;
        do {
          next = Math.floor(Math.random() * quotes.length);
        } while (next === prev && quotes.length > 1);
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return at(quotes, index, "quote");
}

type AuthLayoutProps = Omittable<{
  centered?: boolean;
  heading: string;
  subheading: string;
  children: React.ReactNode;
  footerText?: string;
  footerLinkText?: string;
  footerLinkHref?: string;
}>;

export function AuthLayout({
  heading,
  subheading,
  children,
  footerText,
  footerLinkText,
  footerLinkHref,
}: AuthLayoutProps) {
  // Quotes show from the `lg` breakpoint (width ≥ 1024) up.
  const showQuotes = useMediaQuery("lg");
  const quote = useRotatingQuote();
  const router = useRouter();

  return (
    <Box direction="row" minHeight="100dvh" background="background">
      <Box
        direction="column"
        // The form column reads as a distinct panel against the page — the
        // sidebar fill is the one recessed-beside-the-page tone in the system.
        background="sidebar"
        flex={1}
        maxWidth={showQuotes ? 600 : undefined}
      >
        <Box direction="column" flex={1}>
          {/* Row wrapper so the logo button hugs the top-left instead of
              stretching across the column. */}
          <Box direction="row">
            <Box
              as="button"
              padding="lg"
              onClick={() => router.replace("/(auth)/login" as any)}
            >
              <SalesTrakLogo />
            </Box>
          </Box>

          <Box
            direction="column"
            align="center"
            justify="center"
            paddingX="lg"
            flex={1}
          >
            <Box direction="column" gap="xl" fill="width" maxWidth={400}>
              <Box direction="column" gap="sm">
                <Text size="2xl" weight="semibold">
                  {heading}
                </Text>
                <Text variant="muted">{subheading}</Text>
              </Box>

              {children}

              {footerText && footerLinkText && footerLinkHref ? (
                <Box direction="row" align="center" justify="center" gap="xs">
                  <Text variant="muted" size="sm">
                    {footerText}
                  </Text>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => router.replace(footerLinkHref as any)}
                  >
                    {footerLinkText}
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box
            opacity={60} direction="column"
            align="center"
            paddingX="lg"
            paddingBottom="lg"
            paddingTop="xs"
          >
            <LegalLinks />
          </Box>
        </Box>
      </Box>

      {showQuotes && (
        <Box
          direction="column"
          align="center"
          justify="center"
          flex={1}
          paddingX="xl"
          paddingY="2xl"
        >
          <Box direction="column" gap="sm" fill="width" maxWidth={500}>
            <Text size="2xl">&ldquo;{quote.text}&rdquo;</Text>
            <Text variant="muted" weight="medium">
              {quote.author}
            </Text>
          </Box>
        </Box>
      )}
    </Box>
  );
}
